import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { CustomersFacade } from '@domain/application/facade';
import { DocumentType } from '@domain/model/enums/document-type/document-type.enum';
import { BiometryService } from '@modules/business-accounts/services/biometry/biometry.service';
import { SignalRService } from '@modules/business-accounts/services/biometry/signal-r.service';
import { IAuthFactors } from '@utils/interfaces/response/auth-factors.interface';
import { IKbaQuestionsResponse } from '@utils/interfaces/response/kba-questions-response.interface';
import { catchError, filter, from, map, Observable, switchMap, tap } from 'rxjs';
import { IStep } from '../../utils/interfaces/step.interface';
import { TraceService } from '@modules/business-accounts/services/trace/trace.service';
import { TraceError } from '@modules/business-accounts/utils/enums/trace-erros.enum';
import { VisitEvent } from '@modules/business-accounts/utils/classes/visit-event.class';
import { TraceEventName } from '@modules/business-accounts/utils/enums/trace-events.enum';

@Component({
  selector: 'ba-equifax-authorization-step',
  templateUrl: './equifax-authorization-step.component.html',
  styleUrls: ['./equifax-authorization-step.component.scss'],
})
export class EquifaxAuthorizationStepComponent implements OnInit, IStep<void> {
  @Input() documentNumber!: string;
  @Input() documentType!: DocumentType;

  @Output() stepDone = new EventEmitter<void>();

  questions$!: ReturnType<CustomersFacade['fetchKbaQuestions']>;
  questionsForm!: FormArray;
  answerKbaQuestions!: () => void;
  showTooManyAttemptsError: boolean = false;
  showBadAttemptError: boolean = false;

  isBiometryShowed = false;
  isKbaShowed = false;

  biometryEnable = false;
  kbaEnable = false;

  haveAuthError = false;
  isLoading = false;

  @ViewChild('iframe', { static: false }) iframe!: ElementRef;
  iframeSrc: any;

  signalr: any = {};
  url: string = '';

  constructor(
    private readonly customersFacade: CustomersFacade,
    private signalRService: SignalRService,
    private authEndpoint: BiometryService,
    private sanitizer: DomSanitizer,
    private traceService: TraceService
  ) {}

  ngOnInit(): void {
    this.fetchAuthFactorTokenInfo()
      .pipe(
        tap(({ kbaEnable, biometryEnable }: IAuthFactors) => {
          this.biometryEnable = biometryEnable;
        })
      )
      .subscribe(() => {
        if (this.kbaEnable) {
          this.isKbaShowed = true;
          this.fetchQuestions();
        } else if (this.biometryEnable) {
          this.initBiometry();
        }
        if (!this.biometryEnable) this.setDone();
      });
  }

  initBiometry() {
    this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_BIOMETRY));
    this.showBiometry();

    this.fetchAuthFactorTokenInfo()
      .pipe(
        tap(({ biometricInfo: { url } }) => {
          this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }),
        switchMap(({ biometricInfo: { signalr } }) => from(this.signalRService.init(signalr)))
      )
      .subscribe((connectionFlag) => {
        if (connectionFlag) {
          this.waitForTransactionId();
        }
      });
  }

  waitForTransactionId() {
    this.signalRService.signalR$
      .pipe(
        switchMap((transactionId) => this.getOperationService(transactionId)),
        filter((transactionAccepted) => transactionAccepted)
      )
      .subscribe(() => this.setDone());
  }

  fetchAuthFactorTokenInfo(): Observable<IAuthFactors> {
    return this.authEndpoint.fetchAuthFactorTokenInfo(this.documentNumber, this.documentType).pipe(map((data) => data as IAuthFactors));
  }

  getOperationService(transactionId: string) {
    return this.authEndpoint.fetchAuthOperation(transactionId, this.documentNumber, this.documentType);
  }

  fetchQuestions() {
    this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_KBA));
    this.questions$ = this.customersFacade.fetchKbaQuestions(this.documentNumber, this.documentType).pipe(
      tap((res) => {
        this.questionsForm = new FormArray([]);
        const { kba } = res;
        kba.questions.forEach(() => {
          this.questionsForm.push(new FormControl(null, [Validators.required]));
        });
        this.setAnsweringFn(kba.id, kba.questions);
      }),
      catchError(({ error }) => {
        if (error.error.code === 'CUSTOMER_BLOCKED') {
          this.haveAuthError = true;
          this.traceService.registerError(TraceError.KBA_CUSTOMER_BLOCKED, error);
        }
        throw error;
      })
    );
  }

  get canDoAnswer() {
    return this.questionsForm?.valid && !this.showTooManyAttemptsError;
  }

  setAnsweringFn(kbaId: string, questions: IKbaQuestionsResponse['kba']['questions']) {
    this.answerKbaQuestions = () => {
      if (!this.canDoAnswer) return;
      this.isLoading = true;
      this.showBadAttemptError = false;
      this.customersFacade
        .answerKbaQuestions(
          kbaId,
          this.questionsForm.controls.map((control, index) => {
            const answerId = control.value;
            const { id } = questions[index];
            return {
              id,
              answerId,
            };
          })
        )
        .subscribe({
          next: (res) => {
            this.isLoading = false;
            if (res.kba.status === 'APPROVED') {
              this.biometryEnable ? this.initBiometry() : this.setDone();
            } else {
              this.showBadAttemptError = true;
              this.fetchQuestions();
            }
          },
          error: (err) => {
            if (err instanceof HttpErrorResponse) {
              const code = err.error?.error?.code;
              if (code === 'TOO_MANY_ATTEMPTS') {
                this.showTooManyAttemptsError = true;
                this.traceService.registerError(TraceError.KBA_MAX_ATTEMPTS_ERROR, err);
              }
            }
          },
        });
    };
  }

  showBiometry() {
    this.isKbaShowed = false;
    this.isBiometryShowed = true;
  }

  setDone() {
    this.isLoading = false;
    this.stepDone.emit();
  }
}
